import { memo, ReactNode, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { BehaviorSubject } from "rxjs";

const STREAMING_DISABLED_ROUTES = [
  "/warehouse", // All warehouse routes (transfer, replenishment, processing)
  "/pick-sessions", // Order management pick sessions
  "/create-pick-session", // Order management create pick session
];

const PRINT_QUEUE_ROUTES = ["/order-management"];

/**
 * Global observable for streaming enabled state.
 * This allows streaming contexts outside the router to react to route changes.
 */
export const streamingEnabled$ = new BehaviorSubject<boolean>(true);

/**
 * Global observable for print queue enabled state.
 * Only activates print queue polling/streaming on order-management routes.
 */
export const printQueueEnabled$ = new BehaviorSubject<boolean>(false);

/**
 * Hook to get the current streaming enabled state.
 * Can be used outside of React components.
 */
export function getStreamingEnabled(): boolean {
  return streamingEnabled$.getValue();
}

/**
 * Provider that must be placed inside the Router to track route changes
 * and update the global streaming enabled state.
 */
export const StreamingRouteTracker = memo(function StreamingRouteTracker({
  children,
}: {
  children: ReactNode;
}) {
  const location = useLocation();

  useEffect(() => {
    const matchesRoute = (route: string) =>
      location.pathname === route || location.pathname.includes(route);

    const shouldDisable = STREAMING_DISABLED_ROUTES.some(matchesRoute);
    streamingEnabled$.next(!shouldDisable);

    const onPrintQueueRoute = PRINT_QUEUE_ROUTES.some(matchesRoute);
    printQueueEnabled$.next(onPrintQueueRoute);
  }, [location.pathname]);

  return <>{children}</>;
});

/**
 * Hook to subscribe to streaming enabled state changes.
 * Use this in streaming contexts to conditionally enable/disable streaming.
 */
export function useStreamingEnabled(): boolean {
  const [enabled, setEnabled] = useState(() => streamingEnabled$.getValue());

  useEffect(() => {
    const subscription = streamingEnabled$.subscribe(setEnabled);
    return () => subscription.unsubscribe();
  }, []);

  return enabled;
}

export function usePrintQueueEnabled(): boolean {
  const [enabled, setEnabled] = useState(() => printQueueEnabled$.getValue());

  useEffect(() => {
    const subscription = printQueueEnabled$.subscribe(setEnabled);
    return () => subscription.unsubscribe();
  }, []);

  return enabled;
}
